// MCHD Room Scheduler - Block Form Component
// Form for creating and editing time blocks

import React, { useEffect } from 'react';
import {
  Paper,
  Stack,
  TextInput,
  Textarea,
  Group,
  Button,
  Text,
  Alert,
  Divider,
  Title,
  Badge,
  Grid,
  Card,
  ThemeIcon,
  Switch,
  Select,
} from '@mantine/core';
import {
  IconBlockquote,
  IconCalendar,
  IconCheck,
  IconX,
  IconInfoCircle,
  IconClock,
  IconMapPin,
} from '@tabler/icons-react';

// Import hooks
import { useBlockById } from '../../hooks/admin/useBlocks';
import { useRoomsForSelection } from '../../hooks/admin/useRoomManagement';

// Import types
import type {
  BlockWithStats,
  CreateBlockData,
} from '../../api/endpoints/blocks';

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Safely format date to YYYY-MM-DD format
 * @param date - Date string or Date object
 * @returns Formatted date string or empty string
 */
function safeFormatDate(date: any): string {
  try {
    if (!date) return '';
    
    if (typeof date === 'string') {
      return date.split('T')[0];
    }
    
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    
    return '';
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

// =============================================================================
// INTERFACES
// =============================================================================

interface BlockFormProps {
  block?: BlockWithStats | null;
  onSubmit: (data: CreateBlockData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Block Form Component
 * Provides form interface for creating and editing time blocks
 */
export function BlockForm({
  block,
  onSubmit,
  onCancel,
  isLoading = false,
}: BlockFormProps): JSX.Element {
  // =============================================================================
  // HOOKS
  // =============================================================================

  // Get block data for editing
  const { data: blockData } = useBlockById(
    block?.id || '',
    { enabled: !!block?.id && !!block }
  );
  
  // Fetch rooms for selection
  const { data: roomsData, isLoading: roomsLoading } = useRoomsForSelection();

  // =============================================================================
  // FORM STATE
  // =============================================================================

  const [formData, setFormData] = React.useState<CreateBlockData>({
    name: '',
    description: '',
    roomId: '',
    startDate: '',
    endDate: '',
    isActive: true,
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});

  // =============================================================================
  // EFFECTS
  // =============================================================================

  // Load block data when editing
  useEffect(() => {
    try {
      if (blockData?.data) {
        const data = blockData.data;
        setFormData({
          name: data?.name || '',
          description: data?.description || '',
          roomId: (data as any)?.roomId || '',
          startDate: safeFormatDate(data?.startDate),
          endDate: safeFormatDate(data?.endDate),
          isActive: data?.isActive ?? true,
        });
      } else if (block) {
        setFormData({
          name: block?.name || '',
          description: block?.description || '',
          roomId: (block as any)?.roomId || '',
          startDate: safeFormatDate(block?.startDate),
          endDate: safeFormatDate(block?.endDate),
          isActive: block?.isActive ?? true,
        });
      }
    } catch (error) {
      console.error('Error loading block data:', error);
      // Set default form data on error
      setFormData({
        name: '',
        description: '',
        roomId: '',
        startDate: '',
        endDate: '',
        isActive: true,
      });
    }
  }, [blockData, block]);

  // =============================================================================
  // HANDLERS
  // =============================================================================

  /**
   * Handle form field changes
   */
  const handleFieldChange = (field: keyof CreateBlockData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  /**
   * Validate form data
   */
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData['name'].trim()) {
      newErrors['name'] = 'Block name is required';
    }

    if (!formData['roomId']) {
      newErrors['roomId'] = 'Room selection is required';
    }

    if (!formData['startDate']) {
      newErrors['startDate'] = 'Start date is required';
    }

    if (!formData['endDate']) {
      newErrors['endDate'] = 'End date is required';
    }

    if (formData['startDate'] && formData['endDate'] && formData['startDate'] >= formData['endDate']) {
      newErrors['endDate'] = 'End date must be after start date';
    }

    // Check if the date range is reasonable (not more than 1 year)
    if (formData['startDate'] && formData['endDate']) {
      const start = new Date(formData['startDate']);
      const end = new Date(formData['endDate']);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays > 365) {
        newErrors['endDate'] = 'Block duration cannot exceed 1 year';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Convert dates to ISO format
      const submitData: CreateBlockData = {
        ...formData,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: new Date(formData.endDate).toISOString(),
      };
      
      onSubmit(submitData);
    }
  };

  /**
   * Handle form reset
   */
  const handleReset = () => {
    setFormData({
      name: '',
      description: '',
      roomId: '',
      startDate: '',
      endDate: '',
      isActive: true,
    });
    setErrors({});
  };

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  const isEditing = !!block;
  const duration = formData.startDate && formData.endDate 
    ? Math.ceil((new Date(formData.endDate).getTime() - new Date(formData.startDate).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Paper p="lg" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={3}>
              {isEditing ? 'Edit Block' : 'Create New Block'}
            </Title>
            <Text c="dimmed" size="sm">
              {isEditing ? 'Update block details' : 'Configure a new time block'}
            </Text>
          </div>
          {isEditing && (
            <Badge color="blue" variant="light">
              Editing
            </Badge>
          )}
        </Group>

        <Divider />

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            {/* Block Details */}
            <Card withBorder p="md">
              <Group mb="md">
                <ThemeIcon color="blue" variant="light">
                  <IconBlockquote size={16} />
                </ThemeIcon>
                <Text fw={500}>Block Details</Text>
              </Group>

              <Grid>
                <Grid.Col span={12}>
                  <TextInput
                    label="Block Name"
                    placeholder="Enter block name (e.g., Q1 2024, Summer Session, Emergency Period)"
                    value={formData['name']}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    error={errors['name']}
                    required
                    leftSection={<IconBlockquote size={16} />}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Select
                    label="Room"
                    placeholder="Select a room for this block"
                    data={roomsData?.map((room: any) => ({
                      value: room.id,
                      label: `${room.floor?.building?.name || 'Unknown'} - ${room.floor?.name || 'Unknown'} - Room ${room.number}`
                    })) || []}
                    value={formData['roomId']}
                    onChange={(value) => handleFieldChange('roomId', value || '')}
                    error={errors['roomId']}
                    searchable
                    required
                    disabled={roomsLoading || isEditing}
                    leftSection={<IconMapPin size={16} />}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Textarea
                    label="Description"
                    placeholder="Enter block description (optional)"
                    value={formData['description']}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    error={errors['description']}
                    minRows={3}
                    maxRows={6}
                    leftSection={<IconInfoCircle size={16} />}
                  />
                </Grid.Col>
              </Grid>
            </Card>

            {/* Date Range */}
            <Card withBorder p="md">
              <Group mb="md">
                <ThemeIcon color="green" variant="light">
                  <IconCalendar size={16} />
                </ThemeIcon>
                <Text fw={500}>Date Range</Text>
              </Group>

              <Grid>
                <Grid.Col span={6}>
                  <TextInput
                    label="Start Date"
                    type="date"
                    value={formData['startDate']}
                    onChange={(e) => handleFieldChange('startDate', e.target.value)}
                    error={errors['startDate']}
                    required
                    leftSection={<IconCalendar size={16} />}
                  />
                </Grid.Col>
                <Grid.Col span={6}>
                  <TextInput
                    label="End Date"
                    type="date"
                    value={formData['endDate']}
                    onChange={(e) => handleFieldChange('endDate', e.target.value)}
                    error={errors['endDate']}
                    required
                    leftSection={<IconCalendar size={16} />}
                  />
                </Grid.Col>
                {duration > 0 && (
                  <Grid.Col span={12}>
                    <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
                      <Text size="sm">
                        <strong>Duration:</strong> {duration} day{duration !== 1 ? 's' : ''}
                        {duration > 365 && (
                          <Text c="red" size="xs" mt="xs">
                            Warning: Block duration exceeds 1 year
                          </Text>
                        )}
                      </Text>
                    </Alert>
                  </Grid.Col>
                )}
              </Grid>
            </Card>

            {/* Status */}
            <Card withBorder p="md">
              <Group mb="md">
                <ThemeIcon color="orange" variant="light">
                  <IconClock size={16} />
                </ThemeIcon>
                <Text fw={500}>Status</Text>
              </Group>

              <Switch
                label="Active Block"
                description="Enable this block for scheduling"
                checked={formData.isActive}
                onChange={(e) => handleFieldChange('isActive', e.currentTarget.checked)}
                color="green"
                size="md"
              />
            </Card>

            {/* Actions */}
            <Group justify="flex-end" mt="lg">
              <Button
                variant="outline"
                onClick={onCancel}
                leftSection={<IconX size={16} />}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={handleReset}
                leftSection={<IconX size={16} />}
                disabled={isLoading}
              >
                Reset
              </Button>
              <Button
                type="submit"
                loading={isLoading}
                leftSection={<IconCheck size={16} />}
              >
                {isEditing ? 'Update Block' : 'Create Block'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Paper>
  );
}

export default BlockForm;
